'use client';

import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { APP_ENVS } from '@/shared/constants/envs';
import { APP_ROUTES } from '@/shared/constants/navigation';

import { cn } from '@/lib/utils';

import { TOP_FOOTER_COMPARISONS } from '../../constants/comparisons';

// Configuration
const TOTAL_HORIZONTAL_LINES = 8;
const HORIZONTAL_LINE_ON_TOP = 3;
const TOTAL_VERTICAL_LINES = 22;
const VERTICAL_LINES_ON_LEFT = 10;

const CONTAINER_HEIGHT = 580;
const CONTAINER_MAX_WIDTH = typeof window !== 'undefined' ? window.innerWidth : 1600;

type LineConfig = {
	position: Partial<{ top: number; bottom: number; left: number; right: number }>;
	isDashed: boolean;
};

type FooterLink = {
	label: string;
	href: string;
	hideOnMobile?: boolean;
	external?: boolean;
};

type FooterSectionConfig = {
	title: string;
	links: FooterLink[];
};

const FOOTER_SECTIONS: FooterSectionConfig[] = [
	{
		title: 'Product',
		links: [
			{ label: 'Free ATS', href: APP_ROUTES.FREE_ATS, external: false },
			{ label: 'AI ATS', href: APP_ROUTES.AI_ATS, external: false },
			{ label: 'AI screening', href: APP_ROUTES.AI_SCREENING, external: false },
			{ label: 'MCP server', href: APP_ROUTES.MCP, external: false },
			{ label: 'Pricing', href: APP_ROUTES.PRICING, external: false },
			{ label: 'Docs', href: APP_ROUTES.DOCS, external: true },
			{ label: 'Changelog', href: APP_ROUTES.CHANGELOG, external: false },
		],
	},
	{
		title: 'Resources',
		links: [
			...TOP_FOOTER_COMPARISONS.map((comparison) => ({
				label: comparison.title,
				href: comparison.href,
				external: false,
			})),
			{ label: 'Compare', href: APP_ROUTES.COMPARE, external: false },
			{ label: 'Free ATS options', href: APP_ROUTES.BEST_FREE_ATS, external: false },
			{ label: 'ATS MCP servers', href: APP_ROUTES.ATS_WITH_MCP, external: false },
			{
				label: 'Screen job emails',
				href: APP_ROUTES.SCREEN_APPLICATION_EMAILS,
				external: false,
			},
			{ label: 'Screen Google Forms', href: APP_ROUTES.SCREEN_GOOGLE_FORMS, external: false },
			{ label: 'Switch to Binary', href: APP_ROUTES.SWITCH, external: false },
		],
	},
	{
		title: 'Company',
		links: [
			{ label: 'Careers', href: `${APP_ENVS.COMPANY_DOMAIN}/binary`, external: true },
			{
				label: 'Contact',
				href: `mailto:${APP_ENVS.COMPANY_SUPPORT_EMAIL}`,
				hideOnMobile: true,
				external: true,
			},
			{ label: 'Terms', href: APP_ROUTES.TERMS, external: true },
			{ label: 'Privacy', href: APP_ROUTES.PRIVACY, external: true },
			{ label: 'GDPR', href: APP_ROUTES.GDPR, external: true },
			{
				label: 'G2',
				href: 'https://www.g2.com/products/binary',
				external: true,
			},
			{
				label: 'Product Hunt',
				href: 'https://www.producthunt.com/products/binary',
				external: true,
			},
		],
	},
];

export const FooterSection = () => {
	const logoRef = useRef<HTMLDivElement>(null);
	const pathname = usePathname();

	const visibleSections = useMemo(
		() =>
			FOOTER_SECTIONS.map((section) => ({
				...section,
				links: section.links.filter((link) => {
					if (pathname === APP_ROUTES.FREE_ATS && link.href === APP_ROUTES.FREE_ATS) {
						return false;
					}

					if (pathname === link.href) {
						return false;
					}

					return true;
				}),
			})).filter((section) => section.links.length > 0),
		[pathname]
	);

	return (
		<footer
			className="relative mx-auto flex w-full flex-col overflow-hidden px-6 py-10 text-slate-300"
			style={{
				background: '#111111',
				minHeight: CONTAINER_HEIGHT + 'px',
			}}
		>
			<FooterLines logoRef={logoRef} />

			<Gradients />

			<div className="relative z-20 flex flex-1 flex-col items-center gap-8">
				<div className="flex w-full max-w-3xl flex-col items-center gap-8 sm:flex-row sm:flex-wrap sm:items-start sm:justify-center sm:gap-x-16">
					{visibleSections.map((section) => (
						<FooterLinkSection
							key={section.title}
							title={section.title}
							links={section.links}
						/>
					))}
				</div>

				<div className="flex flex-1 items-center justify-center">
					<div
						ref={logoRef}
						className="z-[99] size-[120px] md:size-[240px]"
						style={{
							aspectRatio: '1 / 1',
						}}
					>
						<Image
							src="/images/footer/b-logo.png"
							loading="lazy"
							alt="Logo"
							width={240}
							height={240}
							className="size-full"
							style={{
								aspectRatio: '1 / 1',
							}}
						/>
					</div>
				</div>
			</div>
		</footer>
	);
};

function FooterLinkSection({ title, links }: FooterSectionConfig) {
	return (
		<div className="flex min-w-0 flex-col items-center gap-3 sm:min-w-[140px] sm:items-start">
			<p className="text-xs font-medium uppercase tracking-[0.2em] text-[#6B7280]">{title}</p>
			<nav className="flex flex-col items-center gap-2 sm:items-start">
				{links.map((link) => (
					<Link
						key={link.label}
						href={link.href}
						target={link.external ? '_blank' : undefined}
						rel={link.external ? 'noopener noreferrer' : undefined}
						className={cn(
							'text-sm tracking-wide text-[#AFB7BD] transition-colors duration-200 hover:text-white',
							link.hideOnMobile && 'hidden sm:inline'
						)}
					>
						{link.label}
					</Link>
				))}
			</nav>
		</div>
	);
}

function Gradients() {
	const classname = 'pointer-events-none inset-0 absolute z-[10]';

	return (
		<>
			{/* Vertical gradient */}
			<div
				data-gradient="vertical"
				className={cn(classname)}
				style={{
					background:
						'linear-gradient(270deg, #101111 5.49%, rgba(24, 25, 25, 0.35) 33.73%, rgba(24, 25, 25, 0.35) 71.58%, rgba(16, 17, 17, 0.9) 100%)',
					opacity: '0.38',
				}}
			/>

			{/* horizontal gradient */}
			<div
				data-gradient="horizontal"
				className={cn(classname)}
				style={{
					background:
						'linear-gradient(270deg, rgba(16, 17, 17, 0.9) 0%, rgba(24, 25, 25, 0.35) 29.88%, rgba(24, 25, 25, 0.35) 69.93%, rgba(16, 17, 17, 0.9) 100%)',
					opacity: '0.38',
				}}
			/>

			{/* radial gradient */}
			<div
				data-gradient="radial"
				className={cn(classname)}
				style={{
					background:
						'radial-gradient(7.72% 20.93% at 50% 55.39%, rgba(28, 31, 33, 0.6) 2.34%, #191A1B 100%)',
					opacity: '0.38',
				}}
			/>
		</>
	);
}

function FooterLines({ logoRef }: { logoRef: RefObject<HTMLDivElement | null> }) {
	const containerRef = useRef<HTMLDivElement>(null);

	const [lines, setLines] = useState<{
		horizontal: LineConfig[];
		vertical: LineConfig[];
	}>({
		horizontal: [],
		vertical: [],
	});

	const computeLines = useCallback(() => {
		const logoRect = logoRef.current?.getBoundingClientRect?.();
		if (!logoRect) return;
		// Half the rendered logo size (60px below md, 120px from md up), read from the DOM.
		const gridSpacing = logoRect.width / 2;

		const containerRect = containerRef.current?.getBoundingClientRect?.();
		if (!containerRect) return;

		const netTop = logoRect.top - containerRect.top;
		const netLeft = logoRect.left - containerRect.left;

		const horizontalLines: LineConfig[] = [];
		for (let i = 0; i < HORIZONTAL_LINE_ON_TOP; i++) {
			const position = netTop - i * gridSpacing;
			horizontalLines.push({
				position: {
					top: position,
				},
				isDashed: true,
			});
		}
		for (let i = 0; i < TOTAL_HORIZONTAL_LINES - HORIZONTAL_LINE_ON_TOP; i++) {
			const position = netTop + (i + 1) * gridSpacing;
			horizontalLines.push({
				position: {
					top: position,
				},
				isDashed: true,
			});
		}

		horizontalLines.sort((a, b) => (a.position?.top ?? 0) - (b.position?.top ?? 0));
		horizontalLines.forEach((item, i) =>
			i % 2 === 0 ? (item.isDashed = true) : (item.isDashed = false)
		);

		const verticalLines: LineConfig[] = [];
		for (let i = 0; i < VERTICAL_LINES_ON_LEFT; i++) {
			const position = netLeft - i * gridSpacing;
			verticalLines.push({
				position: {
					left: position,
				},
				isDashed: true,
			});
		}
		for (let i = 0; i < TOTAL_VERTICAL_LINES - VERTICAL_LINES_ON_LEFT; i++) {
			const position = netLeft + (i + 1) * gridSpacing;
			verticalLines.push({
				position: {
					left: position,
				},
				isDashed: true,
			});
		}

		verticalLines.sort((a, b) => (a.position?.left ?? 0) - (b.position?.left ?? 0));
		verticalLines.forEach((item, i) =>
			i % 2 === 0 ? (item.isDashed = true) : (item.isDashed = false)
		);

		setLines({
			horizontal: horizontalLines,
			vertical: verticalLines,
		});
	}, [setLines]);

	useEffect(() => {
		computeLines();
		window.addEventListener('resize', computeLines);

		return () => window.removeEventListener('resize', computeLines);
	}, []);

	return (
		<div
			ref={containerRef}
			className={cn(
				'absolute inset-0 z-[1] flex w-full items-center justify-center overflow-hidden'
			)}
		>
			{/* Logo placeholder */}

			{lines.horizontal.map((line, i) => (
				<Line
					key={`horizontal-${i}`}
					line={line}
					isVertical={false}
				/>
			))}

			{lines.vertical.map((line, i) => (
				<Line
					key={`vertical-${i}`}
					line={line}
					isVertical={true}
				/>
			))}
		</div>
	);
}

const Line = ({ line, isVertical = false }: { line: LineConfig; isVertical: boolean }) => {
	const Comp = isVertical
		? line.isDashed
			? LineVerticalDashed
			: LineVertical
		: line.isDashed
			? LineHorizontalDashed
			: LineHorizontal;

	return (
		<Comp
			line={line}
			className="opacity-20"
		/>
	);
};

const LineVertical = ({ line, className }: { line: LineConfig; className?: string }) => (
	<div
		className={cn('absolute top-0 h-full w-px', className)}
		style={{
			left: line?.position?.left + 'px',
			// background: 'linear-gradient(270deg, #2B2F31 0%, #898F92 50%, #898F92 86.54%, #2B2F31 100%)',
			background:
				'linear-gradient(180deg, hsla(200, 7%, 18%, 1) 0%, hsla(200, 4%, 55%, 1) 31%, hsla(200, 4%, 55%, 1) 65%, hsla(200, 7%, 18%, 1) 100%)',
		}}
	/>
);

const LineHorizontal = ({ line, className }: { line: LineConfig; className?: string }) => (
	<div
		className={cn('absolute left-0 h-px w-full', className)}
		style={{
			top: line?.position?.top + 'px',
			background:
				'linear-gradient(90deg, hsla(200, 7%, 18%, 1) 0%, hsla(200, 4%, 55%, 1) 31%, hsla(200, 4%, 55%, 1) 65%, hsla(200, 7%, 18%, 1) 100%)',
			// background: 'linear-gradient(270deg, #2B2F31 0%, #898F92 50%, #898F92 86.54%, #2B2F31 100%)',
		}}
	/>
);

const LineVerticalDashed = ({ line, className }: { line: LineConfig; className?: string }) => (
	<div
		className={cn(
			'absolute top-1/2 flex h-full w-px -translate-y-1/2 items-center justify-center',
			className
		)}
		style={{ left: line?.position?.left + 'px' }}
	>
		<svg
			className="absolute left-0 top-1/2 -translate-y-1/2"
			width="1"
			height={`${CONTAINER_HEIGHT}`}
			viewBox={`0 0 1 ${CONTAINER_HEIGHT}`}
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<line
				x1="0.5"
				y1="-2.18557e-08"
				x2="0.500019"
				y2={`${CONTAINER_HEIGHT}`}
				stroke="url(#paint0_linear_576_939)"
				stroke-dasharray="8 8"
			/>
			<defs>
				<linearGradient
					id="paint0_linear_576_939"
					x1="-0.499981"
					y1={`${CONTAINER_HEIGHT}`}
					x2="-0.5"
					y2="2.18557e-08"
					gradientUnits="userSpaceOnUse"
				>
					<stop stop-color="#2B2F31" />
					<stop
						offset="0.5"
						stop-color="#898F92"
					/>
					<stop
						offset="0.865385"
						stop-color="#898F92"
					/>
					<stop
						offset="1"
						stop-color="#2B2F31"
					/>
				</linearGradient>
			</defs>
		</svg>
	</div>
);

const LineHorizontalDashed = ({ line, className }: { line: LineConfig; className?: string }) => (
	<div
		className={cn(
			'absolute left-1/2 flex h-px w-full -translate-x-1/2 items-center justify-center',
			className
		)}
		style={{ top: line?.position?.top + 'px' }}
	>
		<svg
			className="absolute left-1/2 top-0 -translate-x-1/2"
			width={`${CONTAINER_MAX_WIDTH}`}
			height="1"
			viewBox={`0 0 ${CONTAINER_MAX_WIDTH} 1`}
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<line
				x1={`${CONTAINER_MAX_WIDTH}`}
				y1="0.5"
				y2="0.5"
				stroke="url(#paint0_linear_576_957)"
				stroke-dasharray="8 8"
			/>
			<defs>
				<linearGradient
					id="paint0_linear_576_957"
					x1="0"
					y1="-0.5"
					x2={`${CONTAINER_MAX_WIDTH}`}
					y2="-0.5"
					gradientUnits="userSpaceOnUse"
				>
					<stop stop-color="#2B2F31" />
					<stop
						offset="0.5"
						stop-color="#898F92"
					/>
					<stop
						offset="0.865385"
						stop-color="#898F92"
					/>
					<stop
						offset="1"
						stop-color="#2B2F31"
					/>
				</linearGradient>
			</defs>
		</svg>
	</div>
);
